import React from 'react';
import { Bot, Sparkles, Check, ArrowRight, AlertTriangle } from 'lucide-react';
import { aiFeatures } from '../data/mock';
import { Button } from './ui/button';

const AISection = () => {
  const scrollToConsultation = () => {
    const element = document.querySelector('#consultation');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="ai" className="py-16 sm:py-24 bg-slate-950 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-violet-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div>
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-violet-500/10 border border-violet-500/20 mb-6">
              <Sparkles className="h-4 w-4 text-violet-400" />
              <span className="text-violet-400 text-sm font-semibold">AI Solutions</span>
            </div>

            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6">
              Artificial Intelligence for the
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-cyan-400">
                Modern Workforce
              </span>
            </h2>

            {/* Urgency Message */}
            <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 mb-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-amber-200 text-sm leading-relaxed">
                  <span className="font-semibold">Organizations that do not adopt AI strategically will fall behind.</span> We help your team understand, adopt, and apply AI in real workflows to improve efficiency and decision-making.
                </p>
              </div>
            </div>

            <p className="text-slate-400 text-base sm:text-lg mb-8 leading-relaxed">
              From improving productivity and communication to supporting innovation and workforce readiness, 
              we make AI approachable, practical, and useful for your organization.
            </p>

            {/* AI Features */}
            <div className="space-y-3 mb-8">
              {aiFeatures.map((feature, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-violet-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="h-3 w-3 text-violet-400" />
                  </div>
                  <span className="text-slate-300 text-sm sm:text-base">{feature}</span>
                </div>
              ))}
            </div>

            <Button
              onClick={scrollToConsultation}
              className="bg-violet-500 hover:bg-violet-400 text-white font-semibold px-6 py-5 transition-all duration-300 hover:shadow-lg hover:shadow-violet-500/25"
            >
              Schedule a Leadership Strategy Session
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>

          {/* Right Visual */}
          <div className="relative">
            <div className="p-8 sm:p-10 rounded-3xl bg-gradient-to-br from-violet-500/10 to-cyan-500/10 border border-white/10">
              {/* AI Icon */}
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center mx-auto mb-6">
                <Bot className="h-10 w-10 text-white" />
              </div>

              <h3 className="text-xl font-bold text-white text-center mb-4">
                AI is a Strategic Advantage—Not a Trend
              </h3>

              <p className="text-slate-400 text-center text-sm mb-6">
                We bridge the gap between people development and technology adoption, 
                helping your team embrace AI with confidence and practical application.
              </p>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 rounded-xl bg-white/5">
                  <div className="text-2xl font-bold text-violet-400 mb-1">85%</div>
                  <div className="text-slate-400 text-xs">of jobs will require AI skills by 2030</div>
                </div>
                <div className="text-center p-4 rounded-xl bg-white/5">
                  <div className="text-2xl font-bold text-cyan-400 mb-1">40%</div>
                  <div className="text-slate-400 text-xs">productivity boost with AI adoption</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AISection;
